import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Edit, Share2, Trash2, Video, Eye, Scissors, Crown, Image as ImageIcon, Maximize2, X, RefreshCw } from 'lucide-react';
import { toast } from 'react-toastify';

import { Clip, ExportFormat, ExportFormatInfo } from '../types';
import { clipApi, exportApi, formatFileSize, formatDuration, formatTimestamp } from '../services/api';
import { optimizedApi } from '../utils/apiOptimizer';
import StartupLoadingScreen from '../components/StartupLoadingScreen';
import ConfirmModal from '../components/ConfirmModal';
import CustomVideoPlayer from '../components/CustomVideoPlayer';
import SocialMediaUploader from '../components/SocialMediaUploader';
import ClipTimingEditor from '../components/ClipTimingEditor';
import ContentHooksGenerator from '../components/ContentHooksGenerator';
import PostExportUpsellModal from '../components/PostExportUpsellModal';
import { useUserPlan } from '../hooks/useUserPlan';
import { useAuth } from '../contexts/AuthContext';
import PlatformIcon from '../components/PlatformIcon';
import ClipDescription from '../components/ClipDescription';

const ClipDetails: React.FC = () => {
  const { clipId } = useParams<{ clipId: string }>();
  const { isPro, planData } = useUserPlan();
  const { currentUser, isGuest, userProfile, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Debug logging for pro status
  useEffect(() => {
    // console.log('🔍 Pro Status Debug:', {
    //   isPro,
    //   planData,
    //   userProfile: userProfile ? {
    //     plan: userProfile.plan,
    //     hasActiveSubscription: userProfile.hasActiveSubscription,
    //     subscriptionStatus: userProfile.subscriptionStatus
    //   } : null,
    //   currentUser: currentUser ? { uid: currentUser.uid, email: currentUser.email } : null,
    //   isGuest
    // });
  }, [isPro, planData, userProfile, currentUser, isGuest]);

  // Update watermark default when isPro status changes
  useEffect(() => {
    setAddWatermark(!isPro);
  }, [isPro]);
  
  // Pre-populate state with cached data if available to eliminate loading flash
  const cachedClip = clipId ? optimizedApi.cache.getClipSync(clipId) : null;
  
  const [clip, setClip] = useState<Clip | null>(() => 
    cachedClip && typeof cachedClip === 'object' ? cachedClip as Clip : null
  );
  const [sourceVideo, setSourceVideo] = useState<any>(null); // Store source video info
  const [exports, setExports] = useState<any>(null);
  const [availableFormats, setAvailableFormats] = useState<ExportFormatInfo[]>([]);
  const [loading, setLoading] = useState(!cachedClip); // Only show loading if no cached data
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState<Record<string, boolean>>({});
  const [editing, setEditing] = useState(false);
  const [editingTiming, setEditingTiming] = useState(false);
  const [livePreviewClip, setLivePreviewClip] = useState<Clip | null>(null);
  const [autoCaptions, setAutoCaptions] = useState(false);
  const [addWatermark, setAddWatermark] = useState(!isPro); // Free users default to true, Pro users default to false
  const [editForm, setEditForm] = useState({
    title: (cachedClip as Clip)?.title || '',
    description: (cachedClip as Clip)?.description || '',
    tags: (cachedClip as Clip)?.tags || [] as string[]
  });
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [generatingThumbnail, setGeneratingThumbnail] = useState(false);
  const [downloadingThumbnail, setDownloadingThumbnail] = useState(false);
  const [thumbnailImageLoading, setThumbnailImageLoading] = useState(false);
  const [thumbnailFullscreen, setThumbnailFullscreen] = useState(false);
  const [thumbnailTextPosition, setThumbnailTextPosition] = useState<'center' | 'top' | 'bottom' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center-left' | 'center-right'>('center');
  const [thumbnailNeedsRegeneration, setThumbnailNeedsRegeneration] = useState(false);
  const [showPostExportUpsell, setShowPostExportUpsell] = useState(false);
  const [hasShownUpsellThisSession, setHasShownUpsellThisSession] = useState(false);
  const [previewModal, setPreviewModal] = useState<{
    isOpen: boolean;
    videoUrl: string;
    title: string;
    format?: string;
  }>({
    isOpen: false,
    videoUrl: '',
    title: '',
    format: undefined
  });
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  const loadClipDetails = useCallback(async () => {
    if (!clipId) return;
    
    try {
      // Only set loading if we don't have cached data
      if (!cachedClip) {
        setLoading(true);
      }
      
      // Clear any previous errors
      setError(null);
      
      // Prefetch video details and dashboard data for smooth back navigation
      optimizedApi.prefetch.dashboardData();
      
      // Load clip details using optimized API with caching
      const clipData = await optimizedApi.getClip(clipId);
      setClip(clipData);
      setEditForm({
        title: clipData.title,
        description: clipData.description,
        tags: clipData.tags
      });

      // Prefetch video details for the parent video
      if (clipData.video_id) {
        optimizedApi.prefetch.videoDetails(clipData.video_id);
      }

      // Load source video information for timeline
      try {
        const { videoApi } = await import('../services/api');
        const videoData = await videoApi.getVideo(clipData.video_id);
        setSourceVideo(videoData);
      } catch (videoError) {
        console.error('Error loading source video:', videoError);
        // Don't fail the whole load if we can't get video info
      }
      
      // Load exports
      const exportsData = await exportApi.getClipExports(clipId);
      setExports(exportsData);
      
      // Check if any exports that might have been marked as "exporting" are actually complete
      // This handles cases where user left the page during export and returned later
      const currentExports = exportsData?.exports || [];
      const updatedExporting = { ...exporting };
      let hasStaleExportStates = false;
      
      Object.keys(exporting).forEach(format => {
        if (exporting[format] && format !== 'all') {
          // Check if this format now exists in exports
          const exportExists = currentExports.find((exp: any) => exp.format === format);
          if (exportExists) {
            updatedExporting[format] = false;
            hasStaleExportStates = true;
          }
        }
      });
      
      if (hasStaleExportStates) {
        setExporting(updatedExporting);
      }
      
    } catch (error: any) {
      console.error('Error loading clip details:', error);
      // Show more specific error message
      const errorMessage = error?.response?.data?.detail || error?.message || 'Failed to load clip details';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [clipId, cachedClip, exporting]);

  useEffect(() => {
    // Wait for auth to load before fetching clip to ensure proper authentication
    if (clipId && !authLoading) {
      loadClipDetails();
      loadExportFormats();
    }
  }, [clipId, authLoading, loadClipDetails]);

  // Retry handler
  const handleRetry = () => {
    setError(null);
    setLoading(true);
    
    // Clear cache to force fresh load
    if (clipId) {
      optimizedApi.cache.invalidate(`clip_${clipId}`);
    }
    
    loadClipDetails();
  };

  // Only scroll to top when the clip ID changes (actual navigation)
  useEffect(() => {
    if (clipId) {
      window.scrollTo(0, 0);
    }
  }, [clipId]);

  // Load existing custom thumbnail if available
  useEffect(() => {
    const loadExistingThumbnail = async () => {
      // Only load custom_thumbnail_url (not regular thumbnail_url)
      let thumbnailUrl = clip?.custom_thumbnail_url;
      
      // Add cache-busting parameter to Firebase Storage URLs to ensure fresh load
      if (thumbnailUrl && thumbnailUrl.includes('storage.googleapis.com')) {
        thumbnailUrl = `${thumbnailUrl}${thumbnailUrl.includes('?') ? '&' : '?'}t=${Date.now()}`;
      }
      
      if (thumbnailUrl && !thumbnailPreview) {
        try {
          setThumbnailImageLoading(true);
          const token = await currentUser?.getIdToken();
          
          // Only add auth header if it's an API endpoint
          const headers: Record<string, string> = {};
          if (thumbnailUrl.startsWith('/api/') || (thumbnailUrl.startsWith('http') && !thumbnailUrl.includes('storage.googleapis.com'))) {
            headers['Authorization'] = `Bearer ${token}`;
          }
          
          const response = await fetch(thumbnailUrl, { headers });
          
          if (response.ok) {
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            setThumbnailPreview(blobUrl);
          }
        } catch (error) {
          console.error('Failed to load existing custom thumbnail:', error);
        } finally {
          setThumbnailImageLoading(false);
        }
      }
    };
    
    loadExistingThumbnail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clip?.custom_thumbnail_url, currentUser]);

  // Load saved thumbnail text position
  useEffect(() => {
    if (clip?.thumbnail_text_position) {
      setThumbnailTextPosition(clip.thumbnail_text_position as any);
    }
  }, [clip?.thumbnail_text_position]);

  // Cleanup thumbnail blob URL on unmount
  useEffect(() => {
    return () => {
      if (thumbnailPreview && thumbnailPreview.startsWith('blob:')) {
        window.URL.revokeObjectURL(thumbnailPreview);
      }
    };
  }, [thumbnailPreview]);

  // Handle ESC key to close fullscreen thumbnail and disable scroll
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && thumbnailFullscreen) {
        e.preventDefault();
        e.stopPropagation();
        setThumbnailFullscreen(false);
      }
    };

    if (thumbnailFullscreen) {
      // Disable body scroll when fullscreen is open
      document.body.style.overflow = 'hidden';
      // Use capture phase to intercept ESC before other handlers
      window.addEventListener('keydown', handleEscape, true);
      return () => {
        document.body.style.overflow = 'unset';
        window.removeEventListener('keydown', handleEscape, true);
      };
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [thumbnailFullscreen]);

  // Mark thumbnail as needing regeneration when text position changes from saved position (if preview already exists)
  useEffect(() => {
    if (thumbnailPreview && !generatingThumbnail) {
      // Only mark as needing regeneration if position differs from the saved position
      const savedPosition = clip?.thumbnail_text_position || 'center';
      setThumbnailNeedsRegeneration(thumbnailTextPosition !== savedPosition);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thumbnailTextPosition, clip?.thumbnail_text_position]);

  const loadExportFormats = async () => {
    try {
      const formatsData = await exportApi.getExportFormats();
      setAvailableFormats(formatsData.formats);
    } catch (error) {
      console.error('Error loading export formats:', error);
    }
  };

  // Function to check and update export status
  const checkExportStatus = useCallback(async () => {
    if (!clip) return;
    
    try {
      const exportsData = await exportApi.getClipExports(clip.id);
      setExports(exportsData);
      
      // Check if any exports that were marked as "exporting" are now complete
      const currentExports = exportsData?.exports || [];
      const updatedExporting = { ...exporting };
      let hasUpdates = false;
      
      Object.keys(exporting).forEach(format => {
        if (exporting[format] && format !== 'all') {
          // Check if this format now exists in exports
          const exportExists = currentExports.find((exp: any) => exp.format === format);
          if (exportExists) {
            updatedExporting[format] = false;
            hasUpdates = true;
            toast.success(`Export to ${format} format completed!`);
          }
        }
      });
      
      if (hasUpdates) {
        setExporting(updatedExporting);
      }
    } catch (error) {
      console.error('Error checking export status:', error);
    }
  }, [clip, exporting]);

  // Handle page visibility changes to recover export state
  useEffect(() => {
    let lastHidden = false;
    
    const handleVisibilityChange = () => {
      if (!document.hidden && lastHidden) {
        // Page became visible after being hidden
        // Check if any exports might have completed while away
        const hasActiveExports = Object.values(exporting).some(isExporting => isExporting);
        
        if (hasActiveExports) {
          // Check export status to see if any completed while away
          checkExportStatus();
        }
      }
      lastHidden = document.hidden;
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [clip, exporting, checkExportStatus]); // Dependencies: clip, exporting state, and checkExportStatus

  const handleExport = async (format: ExportFormat) => {
    if (!clip) return;
    
    try {
      setExporting(prev => ({ ...prev, [format]: true }));
      
      // Show progress message for potentially long exports
      const captionText = autoCaptions ? ' with auto captions' : '';
      const watermarkText = addWatermark ? ' with watermark' : '';
      toast.info(`Exporting clip to ${format} format${captionText}${watermarkText}... This may take a few minutes for large clips.`, {
        autoClose: 5000
      });
      
      await exportApi.exportClip({
        clip_id: clip.id,
        format,
        add_watermark: addWatermark,
        auto_captions: autoCaptions
      });
      
      // Wait a moment for database to update, then reload exports with polling
      await new Promise(resolve => setTimeout(resolve, 2000)); // Wait 2 seconds for DB update
      
      // Start polling to verify export completion
      const pollForExport = async (retryCount = 0) => {
        try {
          const exportsData = await exportApi.getClipExports(clip.id);
          setExports(exportsData);
          
          // Check if the export now exists
          const hasExport = exportsData?.exports?.find((exp: any) => exp.format === format);
          if (hasExport) {
            toast.success(`Clip exported to ${format} format!`);
            
            // Show upsell modal for free users after first export (only once per session)
            if (!isPro && !hasShownUpsellThisSession && currentUser && !isGuest) {
              setTimeout(() => {
                setShowPostExportUpsell(true);
                setHasShownUpsellThisSession(true);
              }, 1000); // Small delay to let success toast show first
            }
            
            return;
          }
          
          // If not found and we haven't exceeded retry limit, try again
          if (retryCount < 3) {
            await new Promise(resolve => setTimeout(resolve, 3000)); // Wait 3 seconds between polls
            await pollForExport(retryCount + 1);
          } else {
            // After 3 retries, show a helpful message
            toast.warning(
              `Export may still be processing. Please refresh the page in a moment to check if it completed.`,
              { autoClose: 6000 }
            );
          }
        } catch (pollError) {
          console.error('Polling error:', pollError);
          // If polling fails, show the standard success message
          toast.success(`Export started! If it doesn't appear immediately, please refresh the page.`);
        }
      };
      
      await pollForExport();
      
    } catch (error: any) {
      console.error('Export error:', error);
      
      // Check if it's a timeout error
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        toast.warning(
          `Export is taking longer than expected. It may still be processing in the background. Try refreshing the page in a few minutes to check if it completed.`,
          { autoClose: 8000 }
        );
        
        // Start polling to check if export completed
        const pollForExport = async () => {
          try {
            await new Promise(resolve => setTimeout(resolve, 10000)); // Wait 10 seconds
            const exportsData = await exportApi.getClipExports(clip.id);
            setExports(exportsData);
            
            // Check if the export now exists
            const hasExport = exportsData?.exports?.find((exp: any) => exp.format === format);
            if (hasExport) {
              toast.success(`Export to ${format} format completed!`);
            }
          } catch (pollError) {
            console.error('Polling error:', pollError);
          }
        };
        pollForExport();
      } else {
        const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error';
        toast.error(`Failed to export to ${format} format: ${errorMessage}`);
      }
    } finally {
      setExporting(prev => ({ ...prev, [format]: false }));
    }
  };

  const handleSaveEdit = async () => {
    if (!clip) return;
    
    try {
      const updatedClip = await clipApi.updateClip(clip.id, editForm);
      setClip(updatedClip);
      setEditing(false);
      toast.success('Clip updated successfully!');
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update clip');
    }
  };

  const handleDeleteExport = async (format: string) => {
    if (!clip) return;
    
    setConfirmModal({
      isOpen: true,
      title: 'Delete Export',
      message: `Are you sure you want to delete the ${format} export? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await exportApi.deleteClipExport(clip.id, format);
          
          // Reload exports
          const exportsData = await exportApi.getClipExports(clip.id);
          setExports(exportsData);
          
          toast.success(`${format} export deleted`);
        } catch (error) {
          console.error('Delete export error:', error);
          toast.error('Failed to delete export');
        }
      }
    });
  };

  const handleShare = async () => {
    if (!clip) return;
    
    const shareData = {
      title: clip.title,
      text: clip.description,
      url: window.location.href
    };
    
    try {
      // Try Web Share API first
      if (navigator.share) {
        await navigator.share(shareData);
        toast.success('Shared successfully!');
      } else {
        // Fallback to clipboard
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copied to clipboard!');
      }
    } catch (error) {
      console.error('Share error:', error);
      // Manual fallback
      const textArea = document.createElement('textarea');
      textArea.value = window.location.href;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Link copied to clipboard!');
    }
  };

  const handleAdvancedShare = async (shareType: 'public' | 'private') => {
    if (!clip) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/v1/clips/${clip.id}/share`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          public: shareType === 'public',
          expires_in_hours: shareType === 'private' ? 24 : null
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create share link');
      }

      const data = await response.json();
      
      // Copy share URL to clipboard
      await navigator.clipboard.writeText(data.share_url);
      
      toast.success(
        shareType === 'public' 
          ? 'Public share link created and copied to clipboard!' 
          : 'Private share link created and copied to clipboard! (expires in 24 hours)'
      );
    } catch (error: any) {
      console.error('Advanced share error:', error);
      toast.error('Failed to create share link');
    }
  };

  const handleDownloadOriginal = async () => {
    if (!clip) return;
    
    try {
      // Set loading state
      setExporting(prev => ({ ...prev, original: true }));
      
      await exportApi.downloadClip(clip.id);
      toast.success('Download started!');
    } catch (error: any) {
      console.error('Download error:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error';
      toast.error(`Failed to download clip: ${errorMessage}`);
    } finally {
      // Clear loading state
      setExporting(prev => ({ ...prev, original: false }));
    }
  };

  const handleDownloadExport = async (format: string) => {
    if (!clip) return;
    
    try {
      // Set loading state
      setExporting(prev => ({ ...prev, [format]: true }));
      
      await exportApi.downloadExport(clip.id, format);
      toast.success(`${format} download started!`);
    } catch (error: any) {
      console.error('Download error:', error);
      const errorMessage = error?.response?.data?.detail || error?.message || 'Unknown error';
      toast.error(`Failed to download ${format} export: ${errorMessage}`);
    } finally {
      // Clear loading state
      setExporting(prev => ({ ...prev, [format]: false }));
    }
  };

  const handlePreviewExport = async (format: string) => {
    if (!clip) return;
    
    try {
      const previewData = await exportApi.previewExport(clip.id, format);
      setPreviewModal({
        isOpen: true,
        videoUrl: previewData.file_url,
        title: `${clip.title} - ${format}`,
        format: format
      });
    } catch (error) {
      console.error('Preview error:', error);
      toast.error(`Failed to preview ${format} export`);
    }
  };

  // const handlePreviewOriginal = () => {
  //   if (!clip) return;
    
  //   setPreviewModal({
  //     isOpen: true,
  //     videoUrl: clip.file_url,
  //     title: `${clip.title} - Original`,
  //     format: undefined
  //   });
  // };

  const closePreviewModal = () => {
    setPreviewModal({
      isOpen: false,
      videoUrl: '',
      title: '',
      format: undefined
    });
  };

  // Thumbnail generator handlers
  const handleGenerateThumbnailPreview = async () => {
    if (!clip) return;
    
    try {
      setGeneratingThumbnail(true);
      setThumbnailImageLoading(true);
      setThumbnailNeedsRegeneration(false); // Reset the flag when generating
      
      // Fetch the thumbnail with auth headers and text position
      const token = await currentUser?.getIdToken();
      const response = await fetch(`/api/v1/clips/${clip.id}/thumbnail-preview?t=${Date.now()}&text_position=${thumbnailTextPosition}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to generate thumbnail preview');
      }
      
      // Convert to blob URL for img tag
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Revoke previous blob URL to prevent memory leaks
      if (thumbnailPreview && thumbnailPreview.startsWith('blob:')) {
        window.URL.revokeObjectURL(thumbnailPreview);
      }
      
      setThumbnailPreview(blobUrl);
      
      // Reload clip data to get the updated custom_thumbnail_url and text_position
      try {
        const updatedClipData = await optimizedApi.getClip(clip.id);
        setClip(updatedClipData);
      } catch (e) {
        console.error('Failed to reload clip data:', e);
      }
      
      toast.success('Thumbnail preview generated!');
    } catch (error: any) {
      console.error('Thumbnail preview error:', error);
      toast.error('Failed to generate thumbnail preview');
      setThumbnailImageLoading(false);
    } finally {
      setGeneratingThumbnail(false);
    }
  };

  const handleDownloadThumbnail = async () => {
    if (!clip || !thumbnailPreview) return;
    
    try {
      setDownloadingThumbnail(true);
      
      // If we have a blob URL (preview), download it directly
      if (thumbnailPreview.startsWith('blob:')) {
        // Convert blob URL back to blob
        const response = await fetch(thumbnailPreview);
        const blob = await response.blob();
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Create filename: VideoName_ClipTitle_thumbnail.png
        const videoName = sourceVideo?.original_filename?.replace(/\.[^/.]+$/, '') || sourceVideo?.filename?.replace(/\.[^/.]+$/, '') || 'Video';
        const clipTitle = clip.title.replace(/[^a-zA-Z0-9]/g, '_');
        link.download = `${videoName}_${clipTitle}_thumbnail.png`;
        
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        // If it's a URL, fetch and download
        const token = await currentUser?.getIdToken();
        const response = await fetch(thumbnailPreview, {
          headers: token ? {
            'Authorization': `Bearer ${token}`
          } : {}
        });
        
        if (!response.ok) {
          throw new Error('Failed to download thumbnail');
        }
        
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        
        // Create filename: VideoName_ClipTitle_thumbnail.png
        const videoName = sourceVideo?.original_filename?.replace(/\.[^/.]+$/, '') || sourceVideo?.filename?.replace(/\.[^/.]+$/, '') || 'Video';
        const clipTitle = clip.title.replace(/[^a-zA-Z0-9]/g, '_');
        link.download = `${videoName}_${clipTitle}_thumbnail.png`;
        
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
      
      toast.success('Thumbnail downloaded!');
    } catch (error: any) {
      console.error('Thumbnail download error:', error);
      toast.error('Failed to download thumbnail');
    } finally {
      setDownloadingThumbnail(false);
    }
  };

  // Timing editor handlers
  const handleTimingEdit = () => {
    setEditingTiming(true);
    setLivePreviewClip(null); // Reset live preview when starting edit
  };

  const handleTimingCancel = () => {
    setEditingTiming(false);
    setLivePreviewClip(null); // Clear live preview when canceling
  };

  const handleTimingUpdate = (updatedClip: Clip) => {
    setClip(updatedClip);
    setEditingTiming(false);
    setLivePreviewClip(null); // Clear live preview after saving
  };

  const handleLivePreview = (liveClip: Clip) => {
    setLivePreviewClip(liveClip);
  };

  if (loading || authLoading) {
    return (
      <StartupLoadingScreen 
        isVisible={true}
        customTitle="Loading Clip"
        customMessages={[
          "Fetching clip details...",
          "Loading video data...",
          "Preparing export options...",
          "Almost ready..."
        ]}
      />
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-6">
          {/* Error message hidden as requested - only showing action buttons */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={handleRetry}
              className="btn-primary flex items-center justify-center"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry Loading Clip
            </button>
            <Link to="/" className="btn-secondary flex items-center justify-center">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!clip) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Clip not found</p>
        <Link to="/" className="btn-primary mt-4">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      {/* Subtle animated background */}
      <div className="absolute inset-0 dot-pattern-animated opacity-30 pointer-events-none" />
      
      <div className="space-y-8 relative z-10 px-6 sm:px-8 py-8">
        {(!currentUser || isGuest) && (
          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg text-gray-700 text-sm">
            Some features are limited for guests. Sign in to edit titles, share all formats, and auto-upload. Vertical Social Media export is available for guests.
          </div>
        )}
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to={`/videos/${clip.video_id}`} className="btn-secondary">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Video
            </Link>
            
            <div>
              {editing ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => setEditForm(prev => ({ ...prev, title: e.target.value }))}
                    className="text-3xl font-bold bg-transparent border-none outline-none focus:ring-2 focus:ring-red-500 rounded px-2"
                  />
                  <div className="flex space-x-2">
                    <button onClick={handleSaveEdit} className="btn-primary text-sm" disabled={!currentUser || isGuest} title={!currentUser || isGuest ? 'Sign in to use' : undefined}>
                      Save
                    </button>
                    <button 
                      onClick={() => setEditing(false)}
                      className="btn-secondary text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex items-center space-x-2">
                    {sourceVideo?.metadata?.source_platform && (
                      <PlatformIcon 
                        platform={sourceVideo.metadata.source_platform} 
                        size={24}
                      />
                    )}
                    <h1 className="text-3xl font-bold text-gray-900">{clip.title}</h1>
                    <button
                      onClick={() => { if (!currentUser || isGuest) return; setEditing(true); }}
                      disabled={!currentUser || isGuest}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Edit className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="flex items-center space-x-4 mt-2 text-sm text-gray-600">
                    <span>Duration: {formatDuration(clip.duration)}</span>
                    <span>Created: {formatTimestamp(clip.created_at)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          <div className="flex space-x-2">
            <div className={`relative ${(!currentUser || isGuest) ? '' : 'group'}`}>
              <button
                onClick={handleShare}
                disabled={!currentUser || isGuest}
                className="btn-secondary flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                title={!currentUser || isGuest ? 'Sign in to use' : undefined}
              >
                <Share2 className="h-4 w-4 mr-2" />
                Share
              </button>
              
              {/* Advanced Share Dropdown */}
              {(currentUser && !isGuest) && (
                <div className="absolute right-0 mt-1 w-52 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                  <div className="p-2">
                    <div className="space-y-1">
                      <button
                        onClick={() => handleAdvancedShare('public')}
                        className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-50 transition-colors group/item"
                      >
                        <div className="font-medium text-gray-900 group-hover/item:text-red-600">Public Link</div>
                        <div className="text-xs text-gray-500">Anyone can view</div>
                      </button>
                      
                      <button
                        onClick={() => handleAdvancedShare('private')}
                        className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-50 transition-colors group/item"
                      >
                        <div className="font-medium text-gray-900 group-hover/item:text-red-600">Private Link</div>
                        <div className="text-xs text-gray-500">Expires in 24 hours</div>
                      </button>
                      
                      <hr className="border-gray-100 my-1" />
                      
                      <button
                        onClick={handleShare}
                        className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-50 transition-colors group/item"
                      >
                        <div className="font-medium text-gray-900 group-hover/item:text-red-600">Copy Page URL</div>
                        <div className="text-xs text-gray-500">Requires login</div>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Video Player */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Preview
            {editingTiming && livePreviewClip && (
              <span className="ml-2 text-sm text-red-600 font-normal">
                • Live Edit Preview ({formatDuration(livePreviewClip.duration)})
              </span>
            )}
          </h2>
          
          <div className="aspect-video">
            <div className="relative">
              <CustomVideoPlayer
                clip={livePreviewClip || clip} // Use live preview if available
                className="w-full h-full"
              />
              
              {/* Live Preview Indicator */}
              {editingTiming && livePreviewClip && (
                <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full shadow-lg animate-pulse">
                  LIVE EDIT
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Clip Information */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Clip Information</h2>
          
          {editing ? (
            <div className="space-y-4">
              <div>
                <label className="label">Description</label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  className="input-field"
                  rows={3}
                />
              </div>
              
              <div>
                <label className="label">Tags (comma-separated)</label>
                <input
                  type="text"
                  value={editForm.tags.join(', ')}
                  onChange={(e) => setEditForm(prev => ({ 
                    ...prev, 
                    tags: e.target.value.split(',').map(tag => tag.trim()).filter(Boolean)
                  }))}
                  className="input-field"
                  placeholder="gaming, reaction, funny"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Description</h3>
                <ClipDescription description={clip.description} />
              </div>
              
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {clip.tags.map((tag) => {
                    // Map backend tags to user-friendly labels (white, black, red, gray theme)
                    const tagLabels: Record<string, { label: string; className: string }> = {
                      'audio_spike': { label: 'Audio Energy', className: 'bg-red-100 text-red-700' },
                      'speech_highlight': { label: 'Speech Moment', className: 'bg-gray-900 text-white border border-gray-800' },
                      'sentiment_peak': { label: 'Emotional Peak', className: 'bg-red-50 text-red-600 border border-red-200' },
                      'facial_emotion': { label: 'Visual Change', className: 'bg-gray-100 text-gray-700' },
                      'scene_change': { label: 'Visual Change', className: 'bg-gray-100 text-gray-700' },
                      'combined': { label: 'Multi-Signal', className: 'bg-gray-50 text-gray-700 border border-gray-300' },
                    };
                    
                    const tagInfo = tagLabels[tag] || { label: tag, className: 'bg-gray-100 text-gray-600' };
                    
                    return (
                      <span key={tag} className={`text-xs font-medium px-3 py-1 rounded-md ${tagInfo.className}`}>
                        {tagInfo.label}
                      </span>
                    );
                  })}
                </div>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Start Time:</span>
                  <p className="font-medium">{formatDuration(clip.start_time)}</p>
                </div>
                <div>
                  <span className="text-gray-500">End Time:</span>
                  <p className="font-medium">{formatDuration(clip.end_time)}</p>
                </div>
                <div>
                  <span className="text-gray-500">Duration:</span>
                  <p className="font-medium">{formatDuration(clip.duration)}</p>
                </div>
                <div>
                  <span className="text-gray-500">File Size:</span>
                  <p className="font-medium">~{formatFileSize(1024 * 1024)}</p>
                </div>
              </div>

              {/* Timing Editor Button */}
              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    // console.log('🔍 Button clicked - Current status:', { 
                    //   currentUser: !!currentUser, 
                    //   isGuest, 
                    //   isPro, 
                    //   userPlan: userProfile?.plan,
                    //   hasActiveSubscription: userProfile?.hasActiveSubscription 
                    // });
                    
                    if (!currentUser || isGuest) {
                      // console.log('❌ Blocked: Not logged in or guest user');
                      return;
                    }
                    
                    // More explicit pro check
                    const isActuallyPro = userProfile?.plan === 'pro' && userProfile?.hasActiveSubscription;
                    // console.log('🔍 Pro check:', { 
                    //   isPro, 
                    //   isActuallyPro, 
                    //   plan: userProfile?.plan, 
                    //   hasActiveSubscription: userProfile?.hasActiveSubscription 
                    // });
                    
                    if (!isPro && !isActuallyPro) {
                      // console.log('❌ Blocked: Not pro user, redirecting to pricing');
                      navigate('/pricing');
                      return;
                    }
                    
                    // console.log('✅ Allowed: Opening timing editor');
                    handleTimingEdit();
                  }}
                  disabled={!currentUser || isGuest}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                    !currentUser || isGuest
                      ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed opacity-50'
                      : !isPro
                      ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-pointer hover:bg-red-50 hover:border-red-200'
                      : 'bg-red-50 hover:bg-red-100 text-red-700'
                  }`}
                  title={
                    !currentUser || isGuest 
                      ? 'Sign in to use' 
                      : !isPro 
                      ? 'Click to upgrade to Pro'
                      : undefined
                  }
                >
                  {!isPro && currentUser && !isGuest && <Crown className="h-4 w-4 text-red-600" />}
                  <Scissors className="h-4 w-4" />
                  <span>Edit Clip Timing</span>
                  {!isPro && currentUser && !isGuest && <span className="text-xs text-red-600 font-semibold ml-1">Pro</span>}
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  {!isPro && currentUser && !isGuest ? 'Pro feature: Adjust start and end times with precision controls' : 'Adjust start and end times with precision controls'}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Timing Editor Modal */}
        {editingTiming && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <ClipTimingEditor
                clip={clip}
                onClipUpdate={handleTimingUpdate}
                onCancel={handleTimingCancel}
                onLivePreview={handleLivePreview}
              />
            </div>
          </div>
        )}

        {/* Export Options */}
        <div className="card">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">Export Options</h2>
            
            <div className="flex items-center space-x-3">
              {/* Watermark Checkbox */}
              <label className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:border-gray-300 transition-all duration-200 cursor-pointer bg-white">
                <input
                  type="checkbox"
                  checked={addWatermark}
                  onChange={(e) => {
                    if (!isPro) return; // Free users cannot uncheck
                    setAddWatermark(e.target.checked);
                  }}
                  disabled={!isPro}
                  className={`w-4 h-4 rounded border-2 ${
                    !isPro
                      ? 'border-gray-300 bg-gray-100 cursor-not-allowed'
                      : 'border-gray-300 bg-white accent-red-600 hover:border-gray-500 cursor-pointer'
                  } focus:ring-2 focus:ring-red-500 focus:ring-offset-0 focus:border-red-500 transition-colors`}
                />
                <span className={!isPro ? 'text-gray-500' : 'text-gray-700'}>Add Watermark</span>
              </label>

              {/* Auto Captions Pill */}
              <button
                onClick={() => {
                  if (!currentUser || isGuest) return;
                  if (!isPro) {
                    navigate('/pricing');
                    return;
                  }
                  setAutoCaptions(!autoCaptions);
                }}
                disabled={!currentUser || isGuest}
                className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center space-x-2 transition-all duration-200 ${
                  !currentUser || isGuest
                    ? 'bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed opacity-50'
                    : autoCaptions && isPro
                    ? 'bg-red-100 text-red-700 border border-red-200'
                    : isPro
                    ? 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
                    : 'bg-gray-100 text-gray-400 border border-gray-200 cursor-pointer hover:bg-red-50 hover:border-red-200'
                }`}
                title={!currentUser || isGuest ? 'Sign in to use' : undefined}
              >
                {!isPro && <Crown className="w-4 h-4 text-red-600" />}
                <span>Auto Captions</span>
                {!isPro && <span className="text-xs text-red-600 font-semibold ml-1">Pro</span>}
              </button>
            </div>
          </div>
          
          {/* Auto Captions Info */}
          {autoCaptions && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start space-x-2">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-red-800">Auto Captions Enabled</h4>
                  <p className="text-sm text-gray-700 mt-1">
                    AI-generated captions will be added to your exported videos. Captions are positioned optimally:
                  </p>
                  <ul className="text-sm text-gray-700 mt-2 space-y-1">
                    <li>• <strong>Vertical videos:</strong> Bottom third (ideal for TikTok/Shorts)</li>
                    <li>• <strong>Facecam videos:</strong> Top area (above video content)</li>
                    <li>• <strong>Original videos:</strong> Bottom area with proper styling</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {availableFormats.map((format) => {
              const hasExport = exports?.exports?.find((exp: any) => exp.format === format.format);
              const isExporting = exporting[format.format];
              
              // Free users can only use 'vertical' format - disable facecam and original
              const isPremiumFormat = format.format === 'vertical_facecam' || format.format === 'original';
              const isFormatLocked = !isPro && currentUser && !isGuest && isPremiumFormat;
              
              return (
                <div key={format.format} className={`border rounded-lg p-4 ${isFormatLocked ? 'border-gray-300 bg-gray-50 opacity-75' : 'border-gray-200'}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center mb-1">
                        <h3 className={`font-medium ${isFormatLocked ? 'text-gray-500' : 'text-gray-900'}`}>{format.name}</h3>
                        {(format as any).experimental && (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                            Experimental
                          </span>
                        )}
                        {isFormatLocked && (
                          <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                            <Crown className="h-3 w-3" />
                            Pro Only
                          </span>
                        )}
                      </div>
                      <p className={`text-sm ${isFormatLocked ? 'text-gray-500' : 'text-gray-600'}`}>{format.description}</p>
                      <div className={`flex items-center space-x-4 mt-2 text-xs ${isFormatLocked ? 'text-gray-400' : 'text-gray-500'}`}>
                        <span>Resolution: {format.resolution}</span>
                        <span>Aspect: {format.aspect_ratio}</span>
                        {format.max_duration && (
                          <span>Max: {format.max_duration}s</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex flex-col space-y-2">
                      {hasExport ? (
                        <>
                          <button
                            onClick={() => handlePreviewExport(format.format)}
                            className="btn-secondary text-sm flex items-center"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Preview
                          </button>
                          <button
                            onClick={() => handleDownloadExport(format.format)}
                            className="btn-primary text-sm flex items-center"
                            disabled={exporting[format.format]}
                          >
                            {exporting[format.format] ? (
                              <>
                                <div className="loading-spinner mr-1"></div>
                                Downloading...
                              </>
                            ) : (
                              <>
                                <Download className="h-3 w-3 mr-1" />
                                Download
                              </>
                            )}
                          </button>
                          <button
                            onClick={() => handleDeleteExport(format.format)}
                            className="btn-danger text-sm flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={!currentUser || isGuest}
                            title={!currentUser || isGuest ? 'Sign in to delete exports' : undefined}
                          >
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => {
                            if (isFormatLocked) {
                              navigate('/pricing');
                              toast.info('Upgrade to Pro to unlock this export format');
                            } else {
                              handleExport(format.format as ExportFormat);
                            }
                          }}
                          disabled={
                            isExporting || 
                            ((!currentUser || isGuest) && format.format !== 'vertical')
                          }
                          className="btn-primary text-sm flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                          title={
                            isFormatLocked
                              ? 'Upgrade to Pro to unlock this format'
                              : ((!currentUser || isGuest) && format.format !== 'vertical')
                                ? 'Only Vertical Social Media export is available for guests. Sign in for all formats.' 
                                : undefined
                          }
                        >
                          {isExporting ? (
                            <>
                              <div className="loading-spinner mr-1"></div>
                              Exporting...
                            </>
                          ) : isFormatLocked ? (
                            <>
                              <Crown className="h-3 w-3 mr-1" />
                              Upgrade
                            </>
                          ) : (
                            <>
                              <Video className="h-3 w-3 mr-1" />
                              Export
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-1">
                    {format.recommended_for.map((platform) => (
                      <span key={platform} className={`text-xs font-medium px-2 py-1 rounded ${isFormatLocked ? 'bg-gray-200 text-gray-500' : 'bg-gray-100 text-gray-600'}`}>
                        {platform}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Thumbnail Generator */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <ImageIcon className="h-5 w-5 text-red-600" />
                Thumbnail Generator
              </h2>
              <p className="text-sm text-gray-600 mt-1">Create custom vertical thumbnails using your video footage</p>
            </div>
          </div>

          {/* Premium Badge */}
          {!isPro && currentUser && !isGuest && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <Crown className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-red-900">Upgrade to remove watermark</p>
                <p className="text-xs text-red-700 mt-1">Free users get a "CLIPTHIS" watermark. Pro users get clean, watermark-free thumbnails.</p>
              </div>
              <button
                onClick={() => navigate('/pricing')}
                className="btn-primary text-xs px-3 py-1.5 whitespace-nowrap"
              >
                Upgrade
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* What You Get - Left Column (1/3) */}
            <div className="space-y-4">
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200 h-full">
                <p className="font-semibold text-gray-900 mb-3 text-sm flex items-center gap-2">
                  <span className="text-lg">🎬</span>
                  What You Get
                </p>
                <ul className="space-y-2 text-xs text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-bold">✓</span>
                    <span><strong>Real video frame</strong> as background</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-bold">✓</span>
                    <span><strong>Video title</strong> with professional styling</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-bold">✓</span>
                    <span><strong>Vertical 9:16</strong> for TikTok, Reels, Shorts</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-600 font-bold">✓</span>
                    <span><strong>High-quality PNG</strong> (1080x1920)</span>
                  </li>
                  {!isPro && currentUser && !isGuest && (
                    <li className="flex items-start gap-2 mt-3 pt-2 border-t border-gray-300">
                      <Crown className="h-3.5 w-3.5 text-red-600 mt-0.5" />
                      <span className="font-semibold text-red-700">Pro: No watermark</span>
                    </li>
                  )}
                </ul>
              </div>
            </div>

            {/* Thumbnail Preview - Center Column (1/3) */}
            <div className="flex items-center justify-center">
              <div className="border-2 border-gray-200 rounded-xl overflow-hidden bg-white w-full max-w-[220px] aspect-[9/16] flex items-center justify-center outline-none focus:outline-none focus-within:outline-none relative shadow-sm">
                {/* Loading Spinner Overlay */}
                {(generatingThumbnail || thumbnailImageLoading) && (
                  <div className="absolute inset-0 w-full h-full flex items-center justify-center bg-white z-10">
                    <div className="text-center p-4">
                      <div className="loading-spinner mx-auto mb-3" style={{ width: '40px', height: '40px', borderWidth: '3px' }}></div>
                      <p className="text-xs font-medium text-gray-700">
                        {generatingThumbnail ? 'Generating Thumbnail...' : 'Loading Image...'}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">This may take a few seconds</p>
                    </div>
                  </div>
                )}
                
                {/* Thumbnail Image */}
                {thumbnailPreview ? (
                  <>
                    <img
                      key={thumbnailPreview}
                      src={thumbnailPreview}
                      alt="Thumbnail Preview"
                      className="w-full h-full object-cover outline-none focus:outline-none select-none cursor-pointer"
                      draggable="false"
                      onClick={() => setThumbnailFullscreen(true)}
                      onLoad={() => setThumbnailImageLoading(false)}
                      onError={() => {
                        console.error('Failed to load thumbnail preview');
                        setThumbnailPreview(null);
                        setThumbnailImageLoading(false);
                        toast.error('Failed to load thumbnail preview');
                      }}
                    />
                    
                    {/* Fullscreen Button - Always visible in bottom right corner */}
                    <button
                      onClick={() => setThumbnailFullscreen(true)}
                      className="absolute bottom-2 right-2 bg-black bg-opacity-60 hover:bg-opacity-80 text-white p-2 rounded-lg transition-all z-20 shadow-lg"
                      title="View fullscreen"
                    >
                      <Maximize2 className="h-4 w-4" />
                    </button>
                  </>
                ) : !generatingThumbnail && !thumbnailImageLoading ? (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <div className="text-center p-4">
                      <ImageIcon className="h-10 w-10 mx-auto mb-2 opacity-30 text-gray-400" />
                      <p className="text-xs font-medium text-gray-600">Preview Your Thumbnail</p>
                      <p className="text-xs text-gray-500 mt-1">Uses actual footage from your video</p>
                      <p className="text-xs text-gray-400 mt-1">Click generate thumbnail</p>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            {/* Action Buttons - Right Column (1/3) */}
            <div className="flex flex-col justify-center space-y-4">
              {/* Text Position Selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Text Position</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { value: 'top-left', label: '↖', title: 'Top Left' },
                    { value: 'top', label: '↑', title: 'Top Center' },
                    { value: 'top-right', label: '↗', title: 'Top Right' },
                    { value: 'center-left', label: '←', title: 'Center Left' },
                    { value: 'center', label: '●', title: 'Center' },
                    { value: 'center-right', label: '→', title: 'Center Right' },
                    { value: 'bottom-left', label: '↙', title: 'Bottom Left' },
                    { value: 'bottom', label: '↓', title: 'Bottom Center' },
                    { value: 'bottom-right', label: '↘', title: 'Bottom Right' },
                  ].map((pos) => (
                    <button
                      key={pos.value}
                      onClick={() => setThumbnailTextPosition(pos.value as any)}
                      className={`py-2.5 px-3 rounded-lg text-base font-medium transition-all border-2 ${
                        thumbnailTextPosition === pos.value
                          ? 'bg-red-600 text-white border-red-600 shadow-sm'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-red-300 hover:bg-red-50'
                      }`}
                      title={pos.title}
                      type="button"
                      disabled={generatingThumbnail}
                    >
                      {pos.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500">Select text placement on thumbnail</p>
              </div>

              {/* Generate Button */}
              <button
                onClick={handleGenerateThumbnailPreview}
                disabled={generatingThumbnail || (!!thumbnailPreview && !thumbnailNeedsRegeneration)}
                className={`w-full btn-primary flex items-center justify-center py-2.5 font-medium rounded-lg shadow-sm disabled:opacity-50 disabled:cursor-not-allowed ${
                  thumbnailNeedsRegeneration ? 'ring-4 ring-red-500 ring-offset-2 animate-pulse' : ''
                }`}
                title={
                  !!thumbnailPreview && !thumbnailNeedsRegeneration
                    ? 'Thumbnail already generated for this text position'
                    : undefined
                }
              >
                {generatingThumbnail ? (
                  <>
                    <div className="loading-spinner mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4 mr-2" />
                    {thumbnailNeedsRegeneration ? 'Regenerate Thumbnail' : 'Generate Thumbnail'}
                  </>
                )}
              </button>

              {/* Download Button */}
              <button
                onClick={handleDownloadThumbnail}
                disabled={!thumbnailPreview || downloadingThumbnail}
                className="w-full bg-white hover:bg-gray-50 text-gray-900 flex items-center justify-center py-2.5 font-medium rounded-lg transition-colors border-2 border-gray-200 hover:border-gray-300 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                title={!thumbnailPreview ? 'Generate a preview first' : undefined}
              >
                {downloadingThumbnail ? (
                  <>
                    <div className="loading-spinner mr-2"></div>
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2 text-red-600" />
                    Download
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 text-center">
                Uses real video frames with smart text highlighting
              </p>
            </div>
          </div>
        </div>

        {/* AI Hooks & Captions Generator */}
        <ContentHooksGenerator 
          clipId={clip.id}
          clipTitle={clip.title}
          isPro={isPro}
          onUpgrade={() => navigate('/pricing')}
        />

        {/* Social Media Uploader */}
        <SocialMediaUploader clip={clip} className="mt-6" />

        {/* Preview Modal */}
        {previewModal.isOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold text-gray-900">
                  {previewModal.title}
                </h3>
                <button
                  onClick={closePreviewModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-4">
                <div className="aspect-video bg-black rounded-lg overflow-hidden">
                  <CustomVideoPlayer
                    key={previewModal.videoUrl} // Force reload when URL changes
                    src={previewModal.videoUrl}
                    className="w-full h-full"
                  />
                </div>
                
                <div className="mt-4 flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    {previewModal.format && (
                      <span className="bg-gray-100 text-gray-600 text-xs font-medium px-2 py-1 rounded-full mr-2">{previewModal.format}</span>
                    )}
                    Click download to save this video to your device
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={closePreviewModal}
                      className="btn-secondary"
                    >
                      Close
                    </button>
                    {previewModal.format ? (
                      <button
                        onClick={async () => {
                          try {
                            await handleDownloadExport(previewModal.format!);
                            closePreviewModal();
                          } catch (error) {
                            console.error('Download error in preview:', error);
                            toast.error(`Failed to download ${previewModal.format} export`);
                          }
                        }}
                        className="btn-primary flex items-center"
                        disabled={exporting[previewModal.format!]}
                      >
                        {exporting[previewModal.format!] ? (
                          <>
                            <div className="loading-spinner mr-2"></div>
                            Downloading...
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4 mr-2" />
                            Download {previewModal.format}
                          </>
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={async () => {
                          try {
                            await handleDownloadOriginal();
                            closePreviewModal();
                          } catch (error) {
                            console.error('Download error in preview:', error);
                            toast.error('Failed to download original clip');
                          }
                        }}
                        className="btn-primary flex items-center"
                        disabled={exporting.original}
                      >
                        {exporting.original ? (
                          <>
                            <div className="loading-spinner mr-2"></div>
                            Downloading...
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4 mr-2" />
                            Download Original
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Thumbnail Fullscreen Modal with Zoom */}
        {thumbnailFullscreen && thumbnailPreview && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-50"
            onClick={(e) => {
              // Close if clicking the background (not the image)
              if (e.target === e.currentTarget) {
                setThumbnailFullscreen(false);
              }
            }}
          >
            <div className="relative w-full h-full flex items-center justify-center p-4">
              {/* Close Button - Improved UI */}
              <button
                onClick={() => setThumbnailFullscreen(false)}
                className="absolute top-4 right-4 bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-900 p-2.5 rounded-lg transition-all z-10 shadow-lg"
                title="Close fullscreen"
              >
                <X className="h-5 w-5" />
              </button>

              {/* Zoomable Thumbnail - with scrollable container for zoom */}
              <div className="w-full h-full flex items-center justify-center overflow-auto">
                <img
                  src={thumbnailPreview}
                  alt="Thumbnail Fullscreen"
                  className="max-h-full max-w-full object-contain cursor-zoom-in hover:cursor-zoom-in"
                  style={{
                    minWidth: '100%',
                    minHeight: '100%',
                  }}
                  onClick={(e) => {
                    // Toggle zoom on click
                    const img = e.currentTarget;
                    if (img.style.transform === 'scale(2)') {
                      img.style.transform = 'scale(1)';
                      img.style.cursor = 'zoom-in';
                    } else {
                      img.style.transform = 'scale(2)';
                      img.style.cursor = 'zoom-out';
                    }
                  }}
                />
              </div>

              {/* Instructions */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm bg-black bg-opacity-50 px-4 py-2 rounded-lg pointer-events-none">
                Click image to zoom • Scroll to pan • ESC or X to close
              </div>
            </div>
          </div>
        )}

        {/* Confirmation Modal */}
        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal(prev => ({ ...prev, isOpen: false }))}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
        />

        {/* Post-Export Upsell Modal */}
        <PostExportUpsellModal
          isOpen={showPostExportUpsell}
          onClose={() => setShowPostExportUpsell(false)}
          clipPreviewUrl={clip?.file_url}
          hasWatermark={!isPro}
        />
      </div>
    </div>
  );
};

export default ClipDetails; 